import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Users, 
  Shield, Target, Eye, AlertCircle, RefreshCw, ChevronDown, ChevronUp, ArrowUpRight,
  Zap, DollarSign, Award, Lightbulb, Search, ArrowUp,
  Upload, Trash2, Globe, Info, Crown, FileText, Check
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

// Module-level cache so data persists across component mounts
const dataCache = { impressionData: null, keywordData: null, accountId: null, dateRange: null, fetchedAt: null };
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export default function CompetitionDashboard({ selectedAccount }) {
  const { theme } = useTheme();
  const cacheValid = dataCache.accountId === selectedAccount?.id && dataCache.dateRange === 30 && dataCache.fetchedAt && (Date.now() - dataCache.fetchedAt < CACHE_TTL);
  const [impressionData, setImpressionData] = useState(cacheValid ? dataCache.impressionData : null);
  const [keywordData, setKeywordData] = useState(cacheValid ? dataCache.keywordData : null);
  const [loading, setLoading] = useState(!cacheValid);
  const [keywordLoading, setKeywordLoading] = useState(!cacheValid);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [keywordSort, setKeywordSort] = useState('cost');
  const [keywordFilter, setKeywordFilter] = useState('all');
  const [lastFetched, setLastFetched] = useState(cacheValid ? dataCache.fetchedAt : null);

  const fetchImpressionShare = useCallback(async () => {
    if (!selectedAccount?.id) return;
    setLoading(true);
    try {
      const response = await apiCall(`/api/competition/impression-share?days=${dateRange}&accountId=${selectedAccount.id}`);
      const data = await response.json();
      if (data.success) {
        setImpressionData(data);
        dataCache.impressionData = data;
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount?.id, dateRange]);

  const fetchKeywordCompetitiveness = useCallback(async () => {
    if (!selectedAccount?.id) return;
    setKeywordLoading(true);
    try {
      const response = await apiCall(`/api/competition/keyword-competitiveness?days=${dateRange}&accountId=${selectedAccount.id}`);
      const data = await response.json();
      if (data.success) {
        setKeywordData(data);
        dataCache.keywordData = data;
      }
    } catch (err) {
      console.error('Keyword competitiveness error:', err);
    } finally {
      setKeywordLoading(false);
      const now = Date.now();
      setLastFetched(now);
      dataCache.fetchedAt = now;
      dataCache.accountId = selectedAccount?.id;
      dataCache.dateRange = dateRange;
    }
  }, [selectedAccount?.id, dateRange]);

  useEffect(() => {
    // Skip fetch if cache is still valid
    const isCacheValid = dataCache.accountId === selectedAccount?.id
      && dataCache.dateRange === dateRange
      && dataCache.fetchedAt
      && (Date.now() - dataCache.fetchedAt < CACHE_TTL)
      && dataCache.impressionData && dataCache.keywordData;
    if (isCacheValid) {
      setImpressionData(dataCache.impressionData);
      setKeywordData(dataCache.keywordData);
      setLoading(false);
      setKeywordLoading(false);
      return;
    }
    fetchImpressionShare();
    fetchKeywordCompetitiveness();
  }, [selectedAccount?.id, dateRange]);

  const formatPct = (val) => {
    if (val === null || val === undefined) return '—';
    return `${(val * 100).toFixed(1)}%`;
  };

  const getISColor = (val) => {
    if (val === null || val === undefined) return theme.colors.text.tertiary;
    if (val >= 0.7) return '#10b981';
    if (val >= 0.4) return '#f59e0b';
    return '#ef4444';
  };

  if (!selectedAccount?.id) {
    return (
      <div style={{ padding: 40, color: theme.colors.text.secondary, textAlign: 'center' }}>
        Please select a Google Ads account to view competition data.
      </div>
    );
  }

  // Sort keywords
  const getSortedKeywords = () => {
    if (!keywordData?.keywords) return [];
    let keywords = [...keywordData.keywords];

    // Filter
    if (keywordFilter === 'high') keywords = keywords.filter(k => k.competitivePressure === 'high');
    else if (keywordFilter === 'opportunity') keywords = keywords.filter(k => k.opportunity);
    else if (keywordFilter === 'low-qs') keywords = keywords.filter(k => k.qualityScore !== null && k.qualityScore <= 5);

    // Sort
    if (keywordSort === 'cost') keywords.sort((a, b) => b.cost - a.cost);
    else if (keywordSort === 'impressionShare') keywords.sort((a, b) => (a.searchImpressionShare || 0) - (b.searchImpressionShare || 0));
    else if (keywordSort === 'qualityScore') keywords.sort((a, b) => (a.qualityScore || 0) - (b.qualityScore || 0));
    else if (keywordSort === 'bidGap') keywords.sort((a, b) => (b.bidGap || 0) - (a.bidGap || 0));

    return keywords;
  };

  return (
    <div style={{ padding: '30px 40px', maxWidth: 1400 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: theme.colors.text.primary }}>
            Competitor Analysis
          </h1>
          <p style={{ margin: '8px 0 0', color: theme.colors.text.secondary, fontSize: 15 }}>
            Market position, keyword competitiveness, and improvement opportunities
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(parseInt(e.target.value))}
            style={{
              background: theme.colors.bg.secondary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            onClick={() => { dataCache.fetchedAt = null; fetchImpressionShare(); fetchKeywordCompetitiveness(); }}
            style={{
              background: theme.colors.bg.secondary,
              border: `1px solid ${theme.colors.border.primary}`,
              color: theme.colors.text.secondary,
              borderRadius: 8,
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <RefreshCw size={16} />
          </button>
          {lastFetched && !loading && (
            <span style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
              Updated {Math.round((Date.now() - lastFetched) / 60000)}m ago
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          padding: 16,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 12,
          color: '#ef4444',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Real competitors (Auction Insights upload) */}
      <AuctionInsightsPanel selectedAccount={selectedAccount} theme={theme} />

      {/* Market Position Overview Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: theme.colors.text.secondary }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 12 }}>Loading competitive data...</p>
        </div>
      ) : (
        <>
          {impressionData?.summary && (
            <div style={{ marginBottom: 30 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={20} />
                Market Position
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <ISCard
                  label="Search Impression Share"
                  value={impressionData.summary.searchImpressionShare}
                  icon={<Eye size={20} />}
                  description="% of impressions you receive"
                  theme={theme}
                  formatPct={formatPct}
                  getISColor={getISColor}
                />
                <ISCard
                  label="Top Impression Share"
                  value={impressionData.summary.searchTopIS}
                  icon={<TrendingUp size={20} />}
                  description="% shown in top positions"
                  theme={theme}
                  formatPct={formatPct}
                  getISColor={getISColor}
                />
                <ISCard
                  label="Absolute Top IS"
                  value={impressionData.summary.searchAbsoluteTopIS}
                  icon={<ArrowUpRight size={20} />}
                  description="% shown as #1 position"
                  theme={theme}
                  formatPct={formatPct}
                  getISColor={getISColor}
                />
                <ISCard
                  label="Lost IS (Budget)"
                  value={impressionData.summary.budgetLostIS}
                  icon={<TrendingDown size={20} />}
                  description="% lost due to budget"
                  theme={theme}
                  formatPct={formatPct}
                  getISColor={(v) => {
                    if (v === null || v === undefined) return theme.colors.text.tertiary;
                    if (v <= 0.05) return '#10b981';
                    if (v <= 0.2) return '#f59e0b';
                    return '#ef4444';
                  }}
                  invertColor
                />
                <ISCard
                  label="Lost IS (Rank)"
                  value={impressionData.summary.rankLostIS}
                  icon={<Target size={20} />}
                  description="% lost due to ad rank"
                  theme={theme}
                  formatPct={formatPct}
                  getISColor={(v) => {
                    if (v === null || v === undefined) return theme.colors.text.tertiary;
                    if (v <= 0.1) return '#10b981';
                    if (v <= 0.3) return '#f59e0b';
                    return '#ef4444';
                  }}
                  invertColor
                />
              </div>
            </div>
          )}

          {/* Keyword Competitiveness */}
          {keywordLoading ? (
            <div style={{
              background: theme.colors.bg.secondary,
              borderRadius: 16,
              padding: 40,
              textAlign: 'center',
              color: theme.colors.text.secondary,
              border: `1px solid ${theme.colors.border.primary}`,
              marginBottom: 30
            }}>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 8 }}>Loading keyword competitiveness data...</p>
            </div>
          ) : keywordData?.keywords?.length > 0 ? (
            <div style={{ marginBottom: 30 }}>
              {/* Keyword summary cards */}
              <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Search size={20} />
                Keyword Competitiveness
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                <SummaryCard label="Total Keywords" value={keywordData.summary.totalKeywords} theme={theme} />
                <SummaryCard label="High Competition" value={keywordData.summary.highCompetition} theme={theme} color="#ef4444" />
                <SummaryCard label="Medium Competition" value={keywordData.summary.mediumCompetition} theme={theme} color="#f59e0b" />
                <SummaryCard label="Low Quality Score" value={keywordData.summary.lowQualityScore} theme={theme} color="#ef4444" />
                <SummaryCard label="Avg Quality Score" value={keywordData.summary.avgQualityScore || '—'} theme={theme} color={parseFloat(keywordData.summary.avgQualityScore) >= 7 ? '#10b981' : '#f59e0b'} />
                <SummaryCard label="Opportunities" value={keywordData.summary.opportunities} theme={theme} color="#8b5cf6" />
              </div>

              {/* Filter & Sort Controls */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <select
                  value={keywordFilter}
                  onChange={(e) => setKeywordFilter(e.target.value)}
                  style={{
                    background: theme.colors.bg.secondary,
                    color: theme.colors.text.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All keywords</option>
                  <option value="high">High competition only</option>
                  <option value="opportunity">With opportunities</option>
                  <option value="low-qs">Low Quality Score (≤5)</option>
                </select>
                <select
                  value={keywordSort}
                  onChange={(e) => setKeywordSort(e.target.value)}
                  style={{
                    background: theme.colors.bg.secondary,
                    color: theme.colors.text.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  <option value="cost">Sort by spend</option>
                  <option value="impressionShare">Sort by IS (low first)</option>
                  <option value="qualityScore">Sort by QS (low first)</option>
                  <option value="bidGap">Sort by bid gap (high first)</option>
                </select>
              </div>

              {/* Keyword Table */}
              <div style={{
                background: theme.colors.bg.secondary,
                borderRadius: 16,
                overflow: 'hidden',
                border: `1px solid ${theme.colors.border.primary}`
              }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 80px 90px 90px 90px 2fr',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${theme.colors.border.primary}`,
                  background: theme.colors.bg.tertiary,
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.colors.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div>Keyword</div>
                  <div>Campaign</div>
                  <div style={{ textAlign: 'center' }}>QS</div>
                  <div style={{ textAlign: 'right' }}>IS</div>
                  <div style={{ textAlign: 'right' }}>Avg CPC</div>
                  <div style={{ textAlign: 'right' }}>Top Bid</div>
                  <div>Opportunity</div>
                </div>

                {getSortedKeywords().slice(0, 50).map((kw, idx) => (
                  <div
                    key={`${kw.campaignId}_${kw.keyword}_${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 80px 90px 90px 90px 2fr',
                      padding: '12px 20px',
                      borderBottom: `1px solid ${theme.colors.border.primary}`,
                      fontSize: 13,
                      transition: 'background 0.15s',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.tertiary}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Keyword */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CompetitionBadge pressure={kw.competitivePressure} theme={theme} />
                      <div>
                        <div style={{ fontWeight: 500, color: theme.colors.text.primary }}>
                          {kw.keyword}
                        </div>
                        <div style={{ fontSize: 11, color: theme.colors.text.tertiary }}>
                          {String(kw.matchType || '').replace('_', ' ').toLowerCase()} · {kw.impressions?.toLocaleString()} imp · €{kw.cost}
                        </div>
                      </div>
                    </div>
                    {/* Campaign */}
                    <div style={{ color: theme.colors.text.secondary, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {kw.campaignName}
                    </div>
                    {/* Quality Score */}
                    <div style={{ textAlign: 'center' }}>
                      {kw.qualityScore !== null ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32, height: 24,
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 700,
                          background: kw.qualityScore >= 7 ? 'rgba(16, 185, 129, 0.1)' :
                                     kw.qualityScore >= 5 ? 'rgba(245, 158, 11, 0.1)' :
                                     'rgba(239, 68, 68, 0.1)',
                          color: kw.qualityScore >= 7 ? '#10b981' :
                                kw.qualityScore >= 5 ? '#f59e0b' :
                                '#ef4444'
                        }}>
                          {kw.qualityScore}
                        </span>
                      ) : (
                        <span style={{ color: theme.colors.text.tertiary }}>—</span>
                      )}
                    </div>
                    {/* Impression Share */}
                    <div style={{ textAlign: 'right', fontWeight: 600, color: getISColor(kw.searchImpressionShare) }}>
                      {formatPct(kw.searchImpressionShare)}
                    </div>
                    {/* Avg CPC */}
                    <div style={{ textAlign: 'right', color: theme.colors.text.secondary }}>
                      €{kw.avgCpc?.toFixed(2)}
                    </div>
                    {/* Top of Page Bid */}
                    <div style={{ textAlign: 'right' }}>
                      {kw.topOfPageBid !== null ? (
                        <span style={{ color: kw.bidGap > 0.3 ? '#ef4444' : theme.colors.text.secondary }}>
                          €{kw.topOfPageBid?.toFixed(2)}
                          {kw.bidGap > 0.1 && (
                            <span style={{ fontSize: 10, marginLeft: 2 }}>
                              <ArrowUp size={10} style={{ display: 'inline' }} />
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: theme.colors.text.tertiary }}>—</span>
                      )}
                    </div>
                    {/* Opportunity */}
                    <div style={{ fontSize: 12 }}>
                      {kw.opportunity ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          borderRadius: 6,
                          background: 'rgba(139, 92, 246, 0.08)',
                          color: '#8b5cf6',
                          fontSize: 11,
                          lineHeight: 1.4
                        }}>
                          <Lightbulb size={11} />
                          {kw.opportunity}
                        </span>
                      ) : (
                        <span style={{ color: theme.colors.text.tertiary, fontSize: 11 }}>—</span>
                      )}
                    </div>
                  </div>
                ))}

                {getSortedKeywords().length === 0 && (
                  <div style={{ padding: 30, textAlign: 'center', color: theme.colors.text.tertiary }}>
                    No keywords match this filter.
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Campaign Breakdown */}
          {impressionData?.campaigns?.length > 0 && (
            <div style={{ marginBottom: 30 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={20} />
                Impression Share by Campaign
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {impressionData.campaigns.map(campaign => (
                  <CampaignISCard
                    key={campaign.id}
                    campaign={campaign}
                    isExpanded={expandedCampaign === campaign.id}
                    onToggle={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                    theme={theme}
                    formatPct={formatPct}
                    getISColor={getISColor}
                    selectedAccount={selectedAccount}
                    dateRange={dateRange}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Impression Share Metric Card
function ISCard({ label, value, icon, description, theme, formatPct, getISColor }) {
  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 14,
      padding: '20px 22px',
      transition: 'all 0.2s'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: theme.colors.text.secondary, fontWeight: 500 }}>
          {label}
        </div>
        <div style={{ color: getISColor(value) }}>
          {icon}
        </div>
      </div>
      <div style={{
        fontSize: 32,
        fontWeight: 700,
        color: getISColor(value),
        marginBottom: 6
      }}>
        {formatPct(value)}
      </div>
      <div style={{ fontSize: 12, color: theme.colors.text.tertiary }}>
        {description}
      </div>
      {value !== null && value !== undefined && (
        <div style={{
          marginTop: 12,
          height: 6,
          background: theme.colors.bg.tertiary,
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(value * 100, 100)}%`,
            background: getISColor(value),
            borderRadius: 3,
            transition: 'width 0.5s ease'
          }} />
        </div>
      )}
    </div>
  );
}

// Summary Card
function SummaryCard({ label, value, theme, color }) {
  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 10,
      padding: '14px 16px'
    }}>
      <div style={{ fontSize: 11, color: theme.colors.text.tertiary, textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || theme.colors.text.primary }}>
        {value}
      </div>
    </div>
  );
}

// Competition badge (dot indicator)
function CompetitionBadge({ pressure, theme }) {
  const colors = {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#10b981'
  };
  return (
    <div style={{
      width: 8, height: 8, borderRadius: '50%',
      background: colors[pressure] || theme.colors.text.tertiary,
      flexShrink: 0
    }} />
  );
}

// Generate action points based on campaign IS data
function generateActionPoints(campaign) {
  const actions = [];
  const is = campaign.searchImpressionShare;
  const budgetLost = campaign.budgetLostIS;
  const rankLost = campaign.rankLostIS;
  const topIS = campaign.searchTopIS;
  const absTopIS = campaign.searchAbsoluteTopIS;

  if (budgetLost !== null && budgetLost > 0.2) {
    actions.push({
      type: 'critical',
      icon: DollarSign,
      title: 'Increase budget',
      description: `You're losing ${(budgetLost * 100).toFixed(0)}% of impressions due to budget limits. Consider increasing your daily budget to capture more traffic.`,
      impact: 'high'
    });
  } else if (budgetLost !== null && budgetLost > 0.05) {
    actions.push({
      type: 'warning',
      icon: DollarSign,
      title: 'Budget optimization opportunity',
      description: `${(budgetLost * 100).toFixed(0)}% of impressions are lost due to budget. A small budget increase could drive more visibility.`,
      impact: 'medium'
    });
  }

  if (rankLost !== null && rankLost > 0.3) {
    actions.push({
      type: 'critical',
      icon: Award,
      title: 'Improve Ad Rank',
      description: `${(rankLost * 100).toFixed(0)}% of impressions lost due to low ad rank. Focus on improving Quality Score (better ads, landing pages) and consider increasing bids.`,
      impact: 'high'
    });
  } else if (rankLost !== null && rankLost > 0.1) {
    actions.push({
      type: 'warning',
      icon: Award,
      title: 'Ad Rank could be better',
      description: `${(rankLost * 100).toFixed(0)}% lost to ad rank. Review your keyword Quality Scores and ad relevance in the Quality Score Optimizer.`,
      impact: 'medium'
    });
  }

  if (is !== null && is < 0.3) {
    actions.push({
      type: 'critical',
      icon: Eye,
      title: 'Very low market visibility',
      description: `With only ${(is * 100).toFixed(0)}% impression share, you're missing most potential customers. Review both budget and ad rank.`,
      impact: 'high'
    });
  } else if (is !== null && is >= 0.7) {
    actions.push({
      type: 'success',
      icon: Shield,
      title: 'Strong market position',
      description: `At ${(is * 100).toFixed(0)}% impression share, you have strong visibility. Focus on conversion rate optimization to maximize ROI.`,
      impact: 'low'
    });
  }

  if (topIS !== null && is !== null && is > 0.4 && topIS < 0.3) {
    actions.push({
      type: 'warning',
      icon: TrendingUp,
      title: 'Improve top position rate',
      description: `You show up ${(is * 100).toFixed(0)}% of the time but only ${(topIS * 100).toFixed(0)}% in top positions. Increase bids or improve Quality Score.`,
      impact: 'medium'
    });
  }

  if (absTopIS !== null && topIS !== null && topIS > 0.5 && absTopIS < 0.15) {
    actions.push({
      type: 'info',
      icon: Zap,
      title: 'Target #1 position',
      description: `You often appear in top positions but rarely as #1 (${(absTopIS * 100).toFixed(0)}%). Consider Target Impression Share bidding.`,
      impact: 'medium'
    });
  }

  if (campaign.ctr !== null && campaign.ctr < 0.03 && campaign.impressions > 1000) {
    actions.push({
      type: 'warning',
      icon: Lightbulb,
      title: 'Improve ad copy',
      description: `CTR of ${(campaign.ctr * 100).toFixed(1)}% is below average. Test new ad headlines and descriptions.`,
      impact: 'medium'
    });
  }

  if (actions.length === 0) {
    actions.push({
      type: 'success',
      icon: Shield,
      title: 'Campaign looks healthy',
      description: 'No major competitive issues found. Continue monitoring and optimizing for conversions.',
      impact: 'low'
    });
  }

  return actions;
}

// Campaign Impression Share Row with keyword data & action points
function CampaignISCard({ campaign, isExpanded, onToggle, theme, formatPct, getISColor, selectedAccount, dateRange }) {
  const is = campaign.searchImpressionShare;
  const [campaignKeywords, setCampaignKeywords] = useState(null);
  const [kwLoading, setKwLoading] = useState(false);

  // Fetch campaign-specific keyword data when expanded
  useEffect(() => {
    if (isExpanded && !campaignKeywords && selectedAccount?.id) {
      setKwLoading(true);
      apiCall(`/api/competition/keyword-competitiveness?days=${dateRange}&accountId=${selectedAccount.id}&campaignId=${campaign.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setCampaignKeywords(data);
          }
        })
        .catch(err => console.error('Campaign keyword error:', err))
        .finally(() => setKwLoading(false));
    }
  }, [isExpanded, campaignKeywords, selectedAccount?.id, dateRange, campaign.id]);

  const actionPoints = generateActionPoints(campaign);

  const actionColors = {
    critical: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.2)', icon: '#ef4444' },
    warning: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.2)', icon: '#f59e0b' },
    info: { bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.2)', icon: '#3b82f6' },
    success: { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.2)', icon: '#10b981' }
  };

  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      overflow: 'hidden',
      transition: 'all 0.2s'
    }}>
      {/* Main row */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 120px 1fr',
          alignItems: 'center',
          padding: '16px 20px',
          cursor: 'pointer',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.tertiary}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isExpanded ? <ChevronUp size={16} color={theme.colors.text.tertiary} /> : <ChevronDown size={16} color={theme.colors.text.tertiary} />}
          <span style={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: 15 }}>
            {campaign.name}
          </span>
          {actionPoints.some(a => a.type === 'critical') && (
            <span style={{
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 10
            }}>
              Action needed
            </span>
          )}
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: getISColor(is) }}>
            {formatPct(is)}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <div style={{
            flex: 1,
            maxWidth: 200,
            height: 8,
            background: theme.colors.bg.tertiary,
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${is !== null ? Math.min(is * 100, 100) : 0}%`,
              background: getISColor(is),
              borderRadius: 4,
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>
      </div>
      
      {/* Expanded details */}
      {isExpanded && (
        <div style={{
          padding: '0 20px 20px',
          borderTop: `1px solid ${theme.colors.border.primary}`
        }}>
          {/* Metrics grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
            marginTop: 16
          }}>
            <MetricBox label="Impression Share" value={formatPct(campaign.searchImpressionShare)} theme={theme} />
            <MetricBox label="Top IS" value={formatPct(campaign.searchTopIS)} theme={theme} />
            <MetricBox label="Absolute Top IS" value={formatPct(campaign.searchAbsoluteTopIS)} theme={theme} />
            <MetricBox label="Lost IS (Budget)" value={formatPct(campaign.budgetLostIS)} theme={theme} warn={campaign.budgetLostIS > 0.1} />
            <MetricBox label="Lost IS (Rank)" value={formatPct(campaign.rankLostIS)} theme={theme} warn={campaign.rankLostIS > 0.2} />
            <MetricBox label="Impressions" value={campaign.impressions?.toLocaleString()} theme={theme} />
            <MetricBox label="Clicks" value={campaign.clicks?.toLocaleString()} theme={theme} />
            <MetricBox label="Cost" value={`€${campaign.cost?.toLocaleString()}`} theme={theme} />
            <MetricBox label="Conversions" value={campaign.conversions?.toFixed(1)} theme={theme} />
            <MetricBox label="CTR" value={formatPct(campaign.ctr)} theme={theme} />
          </div>

          {/* Top competitive keywords for this campaign */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={16} />
              Top keywords by competitive pressure
            </h4>
            {kwLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: theme.colors.text.tertiary, fontSize: 13 }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginLeft: 8 }}>Loading keyword data...</span>
              </div>
            ) : campaignKeywords?.keywords?.length > 0 ? (
              <div style={{
                borderRadius: 10,
                overflow: 'hidden',
                border: `1px solid ${theme.colors.border.primary}`
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 60px 80px 80px 80px 2fr',
                  padding: '10px 16px',
                  background: theme.colors.bg.tertiary,
                  fontSize: 11,
                  fontWeight: 600,
                  color: theme.colors.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div>Keyword</div>
                  <div style={{ textAlign: 'center' }}>QS</div>
                  <div style={{ textAlign: 'right' }}>IS</div>
                  <div style={{ textAlign: 'right' }}>CPC</div>
                  <div style={{ textAlign: 'right' }}>Top Bid</div>
                  <div>Opportunity</div>
                </div>
                {campaignKeywords.keywords.slice(0, 15).map((kw, idx) => (
                  <div
                    key={`${kw.keyword}_${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 60px 80px 80px 80px 2fr',
                      padding: '10px 16px',
                      fontSize: 12,
                      borderTop: `1px solid ${theme.colors.border.primary}`,
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CompetitionBadge pressure={kw.competitivePressure} theme={theme} />
                      <span style={{ color: theme.colors.text.primary, fontWeight: 500 }}>{kw.keyword}</span>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {kw.qualityScore !== null ? (
                        <span style={{
                          fontWeight: 700, fontSize: 12,
                          color: kw.qualityScore >= 7 ? '#10b981' : kw.qualityScore >= 5 ? '#f59e0b' : '#ef4444'
                        }}>
                          {kw.qualityScore}
                        </span>
                      ) : '—'}
                    </div>
                    <div style={{ textAlign: 'right', fontWeight: 600, color: getISColor(kw.searchImpressionShare), fontSize: 12 }}>
                      {formatPct(kw.searchImpressionShare)}
                    </div>
                    <div style={{ textAlign: 'right', color: theme.colors.text.secondary, fontSize: 12 }}>
                      €{kw.avgCpc?.toFixed(2)}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, color: kw.bidGap > 0.3 ? '#ef4444' : theme.colors.text.secondary }}>
                      {kw.topOfPageBid ? `€${kw.topOfPageBid.toFixed(2)}` : '—'}
                    </div>
                    <div style={{ fontSize: 11 }}>
                      {kw.opportunity ? (
                        <span style={{ color: '#8b5cf6' }}>{kw.opportunity}</span>
                      ) : (
                        <span style={{ color: theme.colors.text.tertiary }}>—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: 16,
                background: theme.colors.bg.tertiary,
                borderRadius: 10,
                color: theme.colors.text.tertiary,
                fontSize: 13,
                textAlign: 'center'
              }}>
                No keyword data available for this campaign.
              </div>
            )}
          </div>

          {/* Action Points */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={16} />
              Action points
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {actionPoints.map((action, idx) => {
                const colors = actionColors[action.type] || actionColors.info;
                const ActionIcon = action.icon;
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '14px 16px',
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 10,
                      alignItems: 'flex-start'
                    }}
                  >
                    <div style={{
                      width: 32, height: 32,
                      borderRadius: 8,
                      background: colors.border,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <ActionIcon size={16} color={colors.icon} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.text.primary, marginBottom: 2 }}>
                        {action.title}
                        {action.impact === 'high' && (
                          <span style={{
                            marginLeft: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: colors.border,
                            color: colors.icon,
                            textTransform: 'uppercase'
                          }}>
                            High impact
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.5 }}>
                        {action.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Auction Insights panel — real competitor names via CSV upload
// ============================================================
function AuctionInsightsPanel({ selectedAccount, theme }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!selectedAccount?.id) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await apiCall(`/api/competition/auction-insights?accountId=${selectedAccount.id}`);
      const json = await res.json();
      if (json.success) setData(json);
      else setErr(json.error || 'Failed to load competitor data');
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doUpload = async (csv, label) => {
    if (!csv || !csv.trim()) { setErr('Please choose a CSV file or paste its contents.'); return; }
    setUploading(true);
    setErr(null);
    setNotice(null);
    try {
      const res = await apiCall(`/api/competition/auction-insights/upload?accountId=${selectedAccount.id}`, {
        method: 'POST',
        body: { csv, label }
      });
      const json = await res.json();
      if (json.success) {
        setNotice(`Imported ${json.upload.competitorCount} competitors.`);
        setPasteText('');
        setShowUpload(false);
        await fetchData();
      } else {
        setErr(json.error || 'Upload failed');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => doUpload(String(reader.result || ''), file.name.replace(/\.csv$/i, ''));
    reader.onerror = () => setErr('Could not read the file.');
    reader.readAsText(file);
    e.target.value = '';
  };

  const onDelete = async (id) => {
    try {
      const res = await apiCall(`/api/competition/auction-insights/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) await fetchData();
    } catch (e) {
      setErr(e.message);
    }
  };

  const latest = data?.latest;

  // Merge "You" into the competitor list, sorted by impression share desc.
  const rows = (() => {
    if (!latest) return [];
    const list = [...(latest.competitors || [])];
    if (latest.you) list.push(latest.you);
    return list.sort((a, b) => {
      const av = a?.impressionShare?.value;
      const bv = b?.impressionShare?.value;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return bv - av;
    });
  })();

  const maxIS = rows.reduce((m, r) => Math.max(m, r?.impressionShare?.value || 0), 0) || 100;

  const cell = (obj) => (obj && obj.label) ? obj.label : '—';

  return (
    <div style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={20} />
          Your real competitors
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.5px'
          }}>
            Auction Insights
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {latest && data?.history?.length > 0 && (
            <select
              value={latest.id}
              onChange={(e) => {
                const id = parseInt(e.target.value, 10);
                apiCall(`/api/competition/auction-insights/${id}`).then(r => r.json()).then(j => {
                  if (j.success) setData(prev => ({ ...prev, latest: j.upload }));
                });
              }}
              style={{
                background: theme.colors.bg.secondary, color: theme.colors.text.primary,
                border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
                padding: '6px 10px', fontSize: 13, cursor: 'pointer'
              }}
            >
              {data.history.map(h => (
                <option key={h.id} value={h.id}>
                  {h.label || h.dateRangeText || `Upload #${h.id}`} · {new Date(h.uploadedAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => { setShowUpload(s => !s); setErr(null); setNotice(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#8b5cf6', color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
            }}
          >
            <Upload size={15} /> Upload report
          </button>
        </div>
      </div>

      {notice && (
        <div style={{
          padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 10, color: '#10b981', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
        }}>
          <Check size={16} /> {notice}
        </div>
      )}
      {err && (
        <div style={{
          padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, color: '#ef4444', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
        }}>
          <AlertCircle size={16} /> {err}
        </div>
      )}

      {/* Upload box */}
      {(showUpload || (!latest && !loading)) && (
        <div style={{
          background: theme.colors.bg.secondary, border: `1px dashed ${theme.colors.border.primary}`,
          borderRadius: 14, padding: 24, marginBottom: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: 'rgba(139,92,246,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <FileText size={18} color="#8b5cf6" />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: 15 }}>
                Import the Auction Insights export
              </div>
              <div style={{ fontSize: 13, color: theme.colors.text.secondary }}>
                Google doesn't share competitor names via API — upload the CSV from your account to see them here.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1
              }}
            >
              {uploading ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={15} />}
              {uploading ? 'Processing…' : 'Choose CSV file'}
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: 'none' }} />
            <button
              onClick={() => setShowHelp(s => !s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
                color: theme.colors.text.secondary, border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 13, cursor: 'pointer'
              }}
            >
              <Info size={15} /> How do I export this?
            </button>
          </div>

          {showHelp && (
            <ol style={{ margin: '16px 0 0', paddingLeft: 20, color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.7 }}>
              <li>Open your account in <strong>Google Ads</strong>.</li>
              <li>Go to <strong>Campaigns → Insights &amp; reports → Auction insights</strong> (or select campaigns/keywords and click <em>Auction insights</em>).</li>
              <li>Pick your date range and segment (account level is fine to start).</li>
              <li>Click the <strong>Download</strong> icon and choose <strong>.csv</strong>.</li>
              <li>Upload that file here — we'll parse the competitor domains automatically.</li>
            </ol>
          )}

          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: theme.colors.text.tertiary }}>
              Or paste the CSV contents
            </summary>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste the full Auction Insights CSV here…"
              rows={6}
              style={{
                width: '100%', marginTop: 10, padding: 12, borderRadius: 8, fontSize: 12,
                fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                background: theme.colors.bg.tertiary, color: theme.colors.text.primary,
                border: `1px solid ${theme.colors.border.primary}`
              }}
            />
            <button
              onClick={() => doUpload(pasteText, 'Pasted report')}
              disabled={uploading || !pasteText.trim()}
              style={{
                marginTop: 8, background: theme.colors.bg.tertiary, color: theme.colors.text.primary,
                border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8, padding: '8px 14px',
                fontSize: 13, fontWeight: 600, cursor: pasteText.trim() ? 'pointer' : 'not-allowed', opacity: pasteText.trim() ? 1 : 0.5
              }}
            >
              Import pasted CSV
            </button>
          </details>
        </div>
      )}

      {/* Competitor table */}
      {loading ? (
        <div style={{
          background: theme.colors.bg.secondary, borderRadius: 16, padding: 40, textAlign: 'center',
          color: theme.colors.text.secondary, border: `1px solid ${theme.colors.border.primary}`
        }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 8 }}>Loading competitor data…</p>
        </div>
      ) : latest && rows.length > 0 ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, color: theme.colors.text.tertiary }}>
              {latest.reportTitle ? `${latest.reportTitle} · ` : ''}
              {latest.dateRangeText || 'Uploaded report'} · {latest.competitorCount} competitors
              {latest.uploadedAt ? ` · imported ${new Date(latest.uploadedAt).toLocaleDateString()}` : ''}
            </div>
            <button
              onClick={() => onDelete(latest.id)}
              title="Delete this upload"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
                color: theme.colors.text.tertiary, border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer'
              }}
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>

          <div style={{
            background: theme.colors.bg.secondary, borderRadius: 16, overflow: 'hidden',
            border: `1px solid ${theme.colors.border.primary}`
          }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2.4fr 1.6fr 1fr 1fr 1fr 1fr',
              padding: '14px 20px', borderBottom: `1px solid ${theme.colors.border.primary}`,
              background: theme.colors.bg.tertiary, fontSize: 11, fontWeight: 600,
              color: theme.colors.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              <div>Competitor</div>
              <div>Impression share</div>
              <div style={{ textAlign: 'right' }} title="How often you both appeared in the same auction">Overlap</div>
              <div style={{ textAlign: 'right' }} title="How often they ranked above you when you both showed">Pos. above</div>
              <div style={{ textAlign: 'right' }} title="How often they appeared at the top of the page">Top of page</div>
              <div style={{ textAlign: 'right' }} title="How often your ad ranked above theirs (or showed when they didn't)">Outranking</div>
            </div>

            {rows.map((r, idx) => {
              const isYou = r.isYou;
              const isVal = r?.impressionShare?.value;
              const barPct = isVal != null ? Math.min((isVal / maxIS) * 100, 100) : 0;
              return (
                <div
                  key={`${r.domain}_${idx}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2.4fr 1.6fr 1fr 1fr 1fr 1fr',
                    padding: '12px 20px', borderBottom: `1px solid ${theme.colors.border.primary}`,
                    fontSize: 13, alignItems: 'center',
                    background: isYou ? 'rgba(139, 92, 246, 0.06)' : 'transparent'
                  }}
                >
                  {/* Competitor */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ width: 20, color: theme.colors.text.tertiary, fontSize: 12, flexShrink: 0 }}>{idx + 1}</span>
                    <div style={{
                      width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                      background: isYou ? 'rgba(139,92,246,0.15)' : theme.colors.bg.tertiary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {isYou ? <Crown size={14} color="#8b5cf6" /> : <Globe size={14} color={theme.colors.text.tertiary} />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontWeight: isYou ? 700 : 500, color: isYou ? '#8b5cf6' : theme.colors.text.primary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {isYou ? 'You' : r.domain}
                      </div>
                    </div>
                  </div>
                  {/* Impression share with bar */}
                  <div>
                    <div style={{ fontWeight: 600, color: theme.colors.text.primary, marginBottom: 4, fontSize: 13 }}>
                      {cell(r.impressionShare)}
                    </div>
                    <div style={{ height: 6, background: theme.colors.bg.tertiary, borderRadius: 3, overflow: 'hidden', maxWidth: 160 }}>
                      <div style={{ height: '100%', width: `${barPct}%`, background: isYou ? '#8b5cf6' : '#3b82f6', borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', color: theme.colors.text.secondary }}>{cell(r.overlapRate)}</div>
                  <div style={{ textAlign: 'right', color: theme.colors.text.secondary }}>{cell(r.positionAboveRate)}</div>
                  <div style={{ textAlign: 'right', color: theme.colors.text.secondary }}>{cell(r.topOfPageRate)}</div>
                  <div style={{ textAlign: 'right', color: theme.colors.text.secondary }}>{cell(r.outrankingShare)}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: theme.colors.text.tertiary }}>
            <Info size={13} />
            Tip: upload a fresh export each month to track how competitors gain or lose ground over time.
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MetricBox({ label, value, theme, warn }) {
  return (
    <div style={{
      background: warn ? 'rgba(239, 68, 68, 0.08)' : theme.colors.bg.tertiary,
      padding: '12px 14px',
      borderRadius: 8,
      border: warn ? '1px solid rgba(239, 68, 68, 0.2)' : 'none'
    }}>
      <div style={{ fontSize: 11, color: theme.colors.text.tertiary, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: warn ? '#ef4444' : theme.colors.text.primary }}>
        {value || '—'}
      </div>
    </div>
  );
}
